import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import BuzzShell, { useBuzzMe } from "./BuzzShell";

const EL2LAT = { α:"a",β:"v",γ:"g",δ:"d",ε:"e",ζ:"z",η:"i",θ:"th",ι:"i",κ:"k",λ:"l",μ:"m",ν:"n",
  ξ:"x",ο:"o",π:"p",ρ:"r",σ:"s",ς:"s",τ:"t",υ:"y",φ:"f",χ:"ch",ψ:"ps",ω:"o" };
const suggestUsername = (name) =>
  String(name || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
    .replace(/[α-ως]/g, ch => EL2LAT[ch] ?? "")
    .replace(/[^a-z0-9._-]+/g, ".").replace(/\.+/g, ".").replace(/^\.|\.$/g, "").slice(0, 20);
const genPassword = () => {
  const chars = "abcdefghjkmnpqrstuvwxyz23456789";
  return Array.from(crypto.getRandomValues(new Uint32Array(8)), n => chars[n % chars.length]).join("");
};

function TeamInner() {
  const { t } = useTranslation();
  const { me } = useBuzzMe();
  const [team, setTeam] = useState([]);
  const [zones, setZones] = useState([]);
  const [modal, setModal] = useState(null); // {mode:'add'} | {mode:'password', user} | {mode:'created', username, password}
  const [form, setForm] = useState({ name: "", username: "", password: genPassword(), allZones: true, zoneIds: [] });
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const [{ data: tm }, { data: zs }] = await Promise.all([
      axios.get(`${config.API_BASE_URL}/buzz/team`),
      axios.get(`${config.API_BASE_URL}/buzz/zones`),
    ]);
    setTeam(tm);
    setZones(zs);
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyLogin = () => {
    navigator.clipboard?.writeText(me?.loginUrl || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const createMember = async () => {
    setError(null);
    try {
      await axios.post(`${config.API_BASE_URL}/buzz/team`, form);
      setModal({ mode: "created", username: form.username, password: form.password });
      load();
    } catch (e) {
      const code = e?.response?.data?.error;
      setError(code === "username-exists" ? t("This username is taken.") :
               code === "invalid-username" ? t("Username: 3-45 latin characters, digits, . _ -") :
               code === "weak-password" ? t("Password must be at least 6 characters.") :
               t("Something went wrong — please try again."));
    }
  };

  const resetPassword = async (user) => {
    const pw = genPassword();
    await axios.put(`${config.API_BASE_URL}/buzz/team/${user.id}`, { password: pw });
    setModal({ mode: "created", username: user.username, password: pw, isReset: true });
  };

  const removeMember = async (user) => {
    if (!window.confirm(`${t("Remove")} ${user.name || user.username};`)) return;
    await axios.delete(`${config.API_BASE_URL}/buzz/team/${user.id}`);
    load();
  };

  const togglePush = async (user) => {
    await axios.put(`${config.API_BASE_URL}/buzz/team/${user.id}`, { push: !user.push });
    load();
  };

  const saveZones = async (user, allZones, zoneIds) => {
    await axios.put(`${config.API_BASE_URL}/buzz/team/${user.id}/zones`, { allZones, zoneIds });
    load();
  };

  const toggleAll = (user) => saveZones(user, !user.allZones, []);

  const toggleZone = (user, zoneId) => {
    const has = user.zones.some(z => z.id === zoneId);
    const next = has ? user.zones.filter(z => z.id !== zoneId).map(z => z.id)
                     : [...user.zones.map(z => z.id), zoneId];
    return saveZones(user, false, next);
  };

  return (
    <>
      {/* Πώς συνδέεται το προσωπικό */}
      <div className="bz-card" style={{ borderLeft: "4px solid #2563eb" }}>
        <h4 style={{ marginBottom: 8 }}>📱 {t("How your staff connects")}</h4>
        <ol className="bz-steps">
          <li>
            {t("They open this link on their phone:")}{" "}
            <b style={{ wordBreak: "break-all" }}>{me?.loginUrl || "…"}</b>{" "}
            <button className="bz-btn ghost" onClick={copyLogin}>{copied ? "✓" : "📋 " + t("Copy")}</button>
          </li>
          <li>{t("They log in with the username & password you create below.")}</li>
          <li>{t("They add the page to their Home Screen and allow notifications — that's how calls reach their phone.")}</li>
          <li>
            ⌚ {t("Every call notification carries two buttons — «✋ On my way» and «✓ Done» — so a call can be handled straight from the lock screen or a paired smartwatch, without opening the app.")}
          </li>
        </ol>
      </div>

      <div className="bz-toolbar">
        <button className="bz-btn primary" onClick={() => {
          setForm({ name: "", username: "", password: genPassword(), zoneIds: [] });
          setError(null);
          setModal({ mode: "add" });
        }}>➕ {t("Add staff member")}</button>
        {zones.length === 0 && (
          <span className="bz-hint">💡 {t("Tip: create zones in the Tables page to target notifications per area.")}</span>
        )}
      </div>

      <div className="bz-tablewrap">
      <table className="bz-table-list">
        <thead>
          <tr>
            <th>{t("User")}</th>
            <th>{t("Push")}</th>
            <th>{t("Zones")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {team.map(u => (
            <tr key={u.id}>
              <td>
                <b>{u.name || u.username}</b>
                <div className="bz-hint">{u.username}</div>
                {u.isAdmin && <span className="bz-chip">admin</span>}
              </td>
              <td>
                {!u.push ? (
                  <button className="bz-btn ghost" title={t("Push is off — tap to enable")}
                    onClick={() => togglePush(u)}>🔕</button>
                ) : u.receivesNothing ? (
                  <span title={t("No zones selected — receives no notifications")}
                    style={{ fontSize: 18 }}>⛔</span>
                ) : (
                  <button className="bz-btn ghost" title={t("Push is on — tap to mute")}
                    onClick={() => togglePush(u)}>🔔</button>
                )}
              </td>
              <td>
                <label style={{ marginRight: 14, fontSize: 14, whiteSpace: "nowrap", fontWeight: 600 }}>
                  <input type="checkbox" checked={u.allZones} onChange={() => toggleAll(u)} />{" "}
                  {t("All zones")}
                </label>
                {zones.map(z => (
                  <label key={z.id}
                    style={{ marginRight: 12, fontSize: 14, whiteSpace: "nowrap", opacity: u.allZones ? 0.4 : 1 }}>
                    <input type="checkbox" disabled={u.allZones}
                      checked={u.allZones || u.zones.some(x => x.id === z.id)}
                      onChange={() => toggleZone(u, z.id)} /> {z.name}
                  </label>
                ))}
                {u.receivesNothing && (
                  <div className="bz-hint" style={{ color: "#dc2626", marginTop: 4 }}>
                    ⛔ {t("No zones selected — receives no notifications")}
                  </div>
                )}
              </td>
              <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                {!u.isAdmin && (
                  <>
                    <button className="bz-btn ghost" title={t("New password")} onClick={() => resetPassword(u)}>🔑</button>{" "}
                    <button className="bz-btn ghost" title={t("Remove")} onClick={() => removeMember(u)}>🗑</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {modal?.mode === "add" && (
        <div className="bz-modal-bg" onClick={() => setModal(null)}>
          <div className="bz-modal" onClick={e => e.stopPropagation()} style={{ textAlign: "left" }}>
            <h3>{t("Add staff member")}</h3>
            <div className="bz-form-row">
              <label>{t("Name")}</label>
              <input type="text" value={form.name} autoFocus
                onChange={e => setForm({ ...form, name: e.target.value, username: suggestUsername(e.target.value) })} />
            </div>
            <div className="bz-form-row">
              <label>{t("Username")}</label>
              <input type="text" value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })} />
            </div>
            <div className="bz-form-row">
              <label>{t("Password")}</label>
              <input type="text" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })} />
              <button className="bz-btn ghost" onClick={() => setForm({ ...form, password: genPassword() })}>🎲</button>
            </div>
            <div className="bz-form-row">
              <label>{t("Zones")}</label>
              <div>
                <label style={{ marginRight: 12, fontWeight: 600 }}>
                  <input type="checkbox" checked={form.allZones}
                    onChange={() => setForm({ ...form, allZones: !form.allZones, zoneIds: [] })} />{" "}
                  {t("All zones")}
                </label>
                {!form.allZones && zones.map(z => (
                  <label key={z.id} style={{ marginRight: 12 }}>
                    <input type="checkbox" checked={form.zoneIds.includes(z.id)}
                      onChange={() => setForm({
                        ...form,
                        zoneIds: form.zoneIds.includes(z.id)
                          ? form.zoneIds.filter(x => x !== z.id)
                          : [...form.zoneIds, z.id],
                      })} /> {z.name}
                  </label>
                ))}
                {!form.allZones && form.zoneIds.length === 0 && (
                  <div className="bz-hint" style={{ color: "#dc2626" }}>
                    ⛔ {t("No zones selected — receives no notifications")}
                  </div>
                )}
              </div>
            </div>
            {error && <div className="bz-banner err">{error}</div>}
            <div className="bz-toolbar" style={{ marginTop: 12 }}>
              <button className="bz-btn primary" onClick={createMember}
                disabled={form.name.length < 2 || form.username.length < 3 || form.password.length < 6}>
                {t("Create")}
              </button>
              <button className="bz-btn" onClick={() => setModal(null)}>{t("Close")}</button>
            </div>
          </div>
        </div>
      )}

      {modal?.mode === "created" && (
        <div className="bz-modal-bg" onClick={() => setModal(null)}>
          <div className="bz-modal" onClick={e => e.stopPropagation()}>
            <h3>{modal.isReset ? "🔑 " + t("New password") : "✅ " + t("Member created")}</h3>
            <p className="bz-hint">{t("Share these once — the password is not shown again:")}</p>
            <div style={{ background: "#f9fafb", borderRadius: 10, padding: 14, margin: "10px 0", fontSize: 15 }}>
              <div>🔗 <b style={{ wordBreak: "break-all" }}>{me?.loginUrl}</b></div>
              <div>👤 <b>{modal.username}</b></div>
              <div>🔒 <b>{modal.password}</b></div>
            </div>
            <button className="bz-btn primary" onClick={() => {
              navigator.clipboard?.writeText(
                `${me?.loginUrl}\n${t("Username")}: ${modal.username}\n${t("Password")}: ${modal.password}`
              );
              setModal(null);
            }}>📋 {t("Copy & close")}</button>
          </div>
        </div>
      )}
    </>
  );
}

export default function BuzzTeam() {
  return <BuzzShell><TeamInner /></BuzzShell>;
}
